import {inject, Injectable} from '@angular/core';
import {
  AuthenticationDetails,
  CodeDeliveryDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  ICognitoUserPoolData,
} from 'amazon-cognito-identity-js';
import {environment} from '../../environments/environment';
import {HttpService} from './http.service';
import {ServerResponse, ServerStatus} from '../models/types.model';
import {catchAwsError} from '../helpers/awsErrors.helper';
import {AuthResponse, convertToIUserData, ITenantServerResponse, IUserData, UserAccessLevel} from '../models/user.model';
import {LanguageService} from './language.service';
import {SupportedLanguages} from '../models/language.model';
import {ToastService} from './toast.service';
import {IPayrollProvidersServerResponse} from '../models/customer.model';
import {PayrollProvidersService} from './payroll-providers.service';

const poolData: ICognitoUserPoolData = {
  UserPoolId: environment.cognito.userPoolId,
  ClientId: environment.cognito.userPoolClientId,
};
if (environment.cognito['userPoolUrl']) {
  poolData.endpoint = environment.cognito['userPoolUrl'];
}

export const userPool = new CognitoUserPool(poolData);
let loggedInUser: CognitoUser | null = null;

@Injectable({
  providedIn: 'root',
})
export class AuthService extends HttpService {
  private languageService = inject(LanguageService);
  private toastService = inject(ToastService);
  private payrollProvidersService = inject(PayrollProvidersService);

  constructor() {
    super();
  }

  async resetPassword(email: string): Promise<ServerResponse> {
    return new Promise((resolve) => {
      const cUser = new CognitoUser({Username: email, Pool: userPool});

      cUser.forgotPassword({
        onSuccess: (data) => {
          resolve({status: ServerStatus.ok, message: ''});
        },
        onFailure: (err: Error) => {
          resolve(catchAwsError(err));
        },
        inputVerificationCode: (data: CodeDeliveryDetails) => {
          resolve({status: ServerStatus.ok, message: data.AttributeName});
        },
      });
    });
  }

  async confirmPassword(email: string, code: string, password: string): Promise<ServerResponse> {
    return new Promise((resolve) => {
      const cUser = new CognitoUser({Username: email, Pool: userPool});

      cUser.confirmPassword(code, password, {
        onSuccess: (data) => {
          resolve({status: ServerStatus.ok, message: ''});
        },
        onFailure: (err: Error) => {
          resolve(catchAwsError(err));
        },
      });
    });
  }

  successfulAuthentication(result: CognitoUserSession): Partial<IUserData> | null {
    const accessToken: CognitoAccessToken = result.getAccessToken();
    const idToken: CognitoIdToken = result.getIdToken();
    const refreshToken: CognitoRefreshToken = result.getRefreshToken();

    // Check if the user is downloadOnly
    if (idToken.payload['custom:userAccessLevel'] == UserAccessLevel.DownloadOnly) {
      this.toastService.showWarning('auth.noAppAccess');
      return null;
    }

    // Set the language for the user
    this.languageService.setLanguage(idToken.payload['custom:userLanguage'] || SupportedLanguages.fr);

    // Store the userAccessLevel from Cognito token
    const userAccessLevel = idToken.payload['custom:userAccessLevel'];
    this.userService.setUserAccessLevel(userAccessLevel);

    this.userService.login(idToken, accessToken, refreshToken);
    const user = {
      username: accessToken.payload['username'],
      token: idToken.getJwtToken(),
      expires: idToken.getExpiration(),
    };
    return user;
  }

  async signIn(email: string, password: string): Promise<AuthResponse | ServerResponse> {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });
    loggedInUser = new CognitoUser({Username: email, Pool: userPool});

    if (environment.cognito['authenticationFlow']) {
      loggedInUser.setAuthenticationFlowType(environment.cognito['authenticationFlow']);
    }

    return new Promise<AuthResponse | ServerResponse>((resolve) => {
      loggedInUser?.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          const user = this.successfulAuthentication(session);
          if (user === null) {
            // User is blocked (e.g. downloadOnly)
            resolve({status: ServerStatus.error, message: ''});
            return;
          }
          resolve({status: ServerStatus.ok, user, message: ''});
        },

        newPasswordRequired: (userAttributes, requiredAttributes) => {
          this.userService.setNewPasswordRequiredUser(email);
          resolve({
            status: ServerStatus.ok,
            code: 'newPasswordRequired',
            message: 'newPasswordRequired',
          });
        },

        onFailure: (err) => {
          loggedInUser = null;
          resolve(catchAwsError(err));
        },
      });
    });
  }

  async getLoggedInUser(email: string, password: string): Promise<AuthResponse | ServerResponse> {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });
    loggedInUser = new CognitoUser({Username: email, Pool: userPool});

    if (environment.cognito['authenticationFlow']) {
      loggedInUser.setAuthenticationFlowType(environment.cognito['authenticationFlow']);
    }

    return new Promise<AuthResponse | ServerResponse>((resolve) => {
      loggedInUser?.authenticateUser(authenticationDetails, {
        onSuccess: (_session: CognitoUserSession) => {
          resolve({status: ServerStatus.ok, message: ''});
        },

        onFailure: (err) => {
          loggedInUser = null;
          resolve(catchAwsError(err));
        },

        totpRequired: (challengeName, challengeParameters) => {
          resolve({status: ServerStatus.ok, code: 'mfaRequired', message: challengeName});
        },
      });
    });
  }

  async logout() {
    const cognitoUser = userPool.getCurrentUser();
    // don't wait for Cognito to clear our tokens
    this.userService.logout();
    // TODO: should be a global signOut when password is changed
    cognitoUser?.signOut(() => {
      console.debug('auth', 'AuthService.login -> Successful logout from Cognito');
    });
    loggedInUser = null;
  }

  async globalLogout() {
    const cognitoUser = userPool.getCurrentUser();
    cognitoUser?.globalSignOut({
      onSuccess: () => {
        this.userService.logout();
      },
      onFailure: (err: Error) => {
        console.debug('auth', 'AuthService.globalLogout -> Cognitor error: ' + err.message);
      },
    });
    loggedInUser = null;
  }

  async setNewPassword(password: string): Promise<ServerResponse> {
    return new Promise((resolve) => {
      loggedInUser?.completeNewPasswordChallenge(
        password,
        {},
        {
          onSuccess: (result) => {
            this.userService.clearNewPasswordRequiredUser();
            resolve({status: ServerStatus.ok, message: ''});
          },
          onFailure: (err) => {
            resolve(catchAwsError(err));
          },
        },
      );
    });
  }

  async changePassword(oldPW: string, newPW: string): Promise<ServerResponse> {
    const cognitoUser = userPool.getCurrentUser();

    return new Promise((resolve) => {
      cognitoUser?.changePassword(oldPW, newPW, (err, result) => {
        if (err) {
          resolve({status: ServerStatus.error, message: err.message});
        } else {
          // no DB interaction needed
          resolve({status: ServerStatus.ok, message: result as string});
        }
      });
    });
  }

  // USER //
  async fetch(): Promise<ITenantServerResponse> {
    const response = (await this.get('/tenant')) as ITenantServerResponse;
    if (response.status === ServerStatus.ok) {
      const user = convertToIUserData(response.body);
      this.userService.setUserData(user);
    }
    return response;
  }

  async fetchPayrollProviders(): Promise<void> {
    const resp = (await this.get('/mandates/payroll-providers')) as IPayrollProvidersServerResponse;
    if (resp.status === ServerStatus.ok) {
      this.payrollProvidersService.setPayrollProviders(resp.body);
    }
  }
}
